import type { ArticleBlock } from "@/lib/types/content";
import { resolveHeadingId } from "@/lib/scroll-to-anchor";
import { parseHtmlToArticleBlocks } from "@/lib/craft/parse-pasted-content";

export type CraftNodeMap = Record<
  string,
  {
    type: { resolvedName: string };
    isCanvas?: boolean;
    props: Record<string, unknown>;
    displayName?: string;
    custom?: Record<string, unknown>;
    hidden?: boolean;
    nodes?: string[];
    linkedNodes?: Record<string, string>;
    parent?: string;
  }
>;

let nodeCounter = 0;

function nextId(prefix: string) {
  nodeCounter += 1;
  return `${prefix}-${nodeCounter}-${Math.random().toString(36).slice(2, 7)}`;
}

export function createEmptyCraftState(): CraftNodeMap {
  return {
    ROOT: {
      type: { resolvedName: "ContainerBlock" },
      isCanvas: true,
      props: { className: "" },
      displayName: "Page",
      custom: {},
      hidden: false,
      nodes: [],
      linkedNodes: {},
    },
  };
}

export function parseCraftState(input: string | object | null | undefined): CraftNodeMap | null {
  if (!input) return null;
  if (typeof input === "string") {
    if (!input.trim()) return null;
    try {
      return JSON.parse(input) as CraftNodeMap;
    } catch {
      return null;
    }
  }
  if (typeof input === "object" && input !== null && "ROOT" in input) {
    return input as CraftNodeMap;
  }
  return null;
}

export function stringifyCraftState(state: CraftNodeMap | string | null | undefined): string {
  if (!state) return JSON.stringify(createEmptyCraftState());
  if (typeof state === "string") {
    const parsed = parseCraftState(state);
    return JSON.stringify(parsed ?? createEmptyCraftState());
  }
  return JSON.stringify(state);
}

export function hasCraftContent(input: string | object | null | undefined): boolean {
  const state = parseCraftState(input);
  if (!state?.ROOT) return false;
  return (state.ROOT.nodes?.length ?? 0) > 0;
}

export function htmlToCraftState(html: string): CraftNodeMap {
  const blocks = parseHtmlToArticleBlocks(html);
  if (blocks.length === 0) {
    const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (text) return blocksToCraftState([{ type: "p", text }]);
    return createEmptyCraftState();
  }
  return blocksToCraftState(blocks);
}

export function resolveCraftStateForEditor(input: {
  craftState?: string | object | null;
  contentBlocks?: ArticleBlock[] | null;
  contentHtml?: string | null;
}): CraftNodeMap {
  let state: CraftNodeMap;
  if (hasCraftContent(input.craftState)) {
    state = parseCraftState(input.craftState)!;
  } else if ((input.contentBlocks ?? []).length > 0) {
    state = blocksToCraftState(input.contentBlocks!);
  } else if (input.contentHtml?.trim()) {
    state = htmlToCraftState(input.contentHtml);
  } else {
    state = createEmptyCraftState();
  }
  return normalizeEditorCraftState(state);
}

const CANVAS_BLOCK_NAMES = new Set(["ContainerBlock", "SectionBlock", "RowBlock", "ColumnBlock"]);

const LEGACY_RESOLVED_NAMES: Record<string, string> = {
  Text: "TextBlock",
  Hero: "HeroBlock",
  Container: "ContainerBlock",
  Image: "ImageBlock",
  Section: "SectionBlock",
  Row: "RowBlock",
  Column: "ColumnBlock",
};

/** Ensure deserialized editor state renders children and uses current block names. */
export function normalizeEditorCraftState(state: CraftNodeMap): CraftNodeMap {
  const next: CraftNodeMap = { ...state };

  for (const [id, node] of Object.entries(state)) {
    const rawName = node.type?.resolvedName;
    const resolvedName = rawName ? (LEGACY_RESOLVED_NAMES[rawName] ?? rawName) : undefined;
    let normalized = { ...node };
    if (resolvedName && resolvedName !== rawName) {
      normalized = { ...normalized, type: { resolvedName } };
    }
    if (id === "ROOT" || (resolvedName && CANVAS_BLOCK_NAMES.has(resolvedName))) {
      normalized = { ...normalized, isCanvas: true };
    }
    next[id] = normalized;
  }

  return next;
}

export function blocksToCraftState(blocks: ArticleBlock[]): CraftNodeMap {
  nodeCounter = 0;
  const state = createEmptyCraftState();
  const childIds: string[] = [];

  for (const block of blocks) {
    if (block.type === "ul") {
      const id = nextId("text");
      state[id] = {
        type: { resolvedName: "TextBlock" },
        props: {
          text: block.items.map((item) => `• ${item}`).join("\n"),
          variant: "ul",
          className: "mt-5 space-y-2.5 text-lg leading-relaxed text-foreground/85",
        },
        displayName: "List",
        custom: {},
        hidden: false,
        nodes: [],
        linkedNodes: {},
        parent: "ROOT",
      };
      childIds.push(id);
      continue;
    }

    const variant = block.type;
    const className =
      variant === "h2"
        ? "mt-12 scroll-mt-28 text-2xl font-bold tracking-tight text-primary md:text-3xl"
        : variant === "h3"
          ? "mt-8 text-xl font-bold tracking-tight text-primary"
          : "mt-5 text-lg leading-relaxed text-foreground/85 md:text-[17px]";

    const id = nextId("text");
    const anchorId =
      variant === "h2" || variant === "h3" ? resolveHeadingId(block.text) : undefined;
    state[id] = {
      type: { resolvedName: "TextBlock" },
      props: {
        text: block.text,
        variant,
        className,
        ...(anchorId ? { anchorId } : {}),
      },
      displayName: variant === "p" ? "Paragraph" : variant.toUpperCase(),
      custom: {},
      hidden: false,
      nodes: [],
      linkedNodes: {},
      parent: "ROOT",
    };
    childIds.push(id);
  }

  state.ROOT.nodes = childIds;
  return state;
}
